import { CommonModule } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, signal, Signal, untracked, ViewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { NotificationComponent } from '../../notifications/notification/notification.component';
import { SettingsService } from '@app/_services/settings.service';
import { SupportMessage } from '@app/_interfaces/chat.interface';
import { getNewId } from '@app/_utils/utils';
import { getDateLabel } from '@app/_services/date';
import { MatDialog } from '@angular/material/dialog';
import { SupportInstructionsComponent } from '../support-instructions/support-instructions.component';
import { CacheService } from '@app/_services/cache.service';
import { Attachment, AttachmentFile } from '@app/_interfaces/generic.interface';
import { DependencyService } from '@app/_services/dependency.service';
import { FirebaseService } from '@app/_services/firebase.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { InputTextComponent } from '@app/components/shared/inputs/input-text/input-text.component';
import { notOnlyWhitespace } from '@app/_directives/form-validator.directive';
import { DataTransformService } from '@app/_services/data-transform.service';
import { FirebaseFunctionService } from '@app/_services/firebase-function.service';
import { ConsolidatedApiService } from '@app/_services/consolidated-api.service';
import { Subject, takeUntil } from 'rxjs';
import { AttachmentListComponent } from '@app/components/addons/attachments/attachment-list/attachment-list.component';
import { InputFileComponent } from '@app/components/shared/inputs/input-file/input-file.component';
import { AttachmentsService } from '@app/_services/attachments.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { EntityVersions } from '@app/_configs/entity-versions';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { LoaderComponent } from '@app/components/shared/loader/loader.component';
import { AttachmentDialogModel, AttachmentViewComponent } from '@app/components/addons/attachments/attachment-view/attachment-view.component';
import { UtilsService } from '@app/_services/utils.service';
import { ModalService } from '@app/_services/modal.service';
import { log } from '@app/_services/logger';

@Component({
  selector: 'app-chat',
  imports: [
    CommonModule,
    SvgComponent,
    NotificationComponent,
    InputTextComponent,
    InputFileComponent,
    AttachmentListComponent,
    NoResultBlockComponent,
    LoaderComponent
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})

export class ChatComponent implements OnInit, OnDestroy {

  groupMessages: Signal<Record<string, SupportMessage[]>> = computed(() => {
    const groupedMessages: Record<string, SupportMessage[]> = {};
    const allMessagesMap = new Map<string, SupportMessage>();

    // 1. Get IDs of messages already synced from server
    const syncedMessages = this.ss.supportMessages();
    const syncedIds = new Set(syncedMessages.map(m => m.id));

    // 2. Add ONLY uploading messages that ARE NOT yet in the synced list
    this.uploadingMessages().forEach(m => {
      if (!syncedIds.has(m.id)) {
        allMessagesMap.set(m.id, m);
      }
    });

    // 3. Add server messages (they overwrite matches and provide the true timestamp)
    syncedMessages.forEach(m => allMessagesMap.set(m.id, m));

    // 4. Sort strictly by Time
    const sortedMessages = Array.from(allMessagesMap.values()).sort((a, b) => {
      const timeA = this.utils.toDate(a.createdAt || new Date()).getTime();
      const timeB = this.utils.toDate(b.createdAt || new Date()).getTime();
      return timeA - timeB;
    });

    // 5. Group by ISO Date
    sortedMessages.forEach(message => {
      const dateObj = this.utils.toDate(message.createdAt || new Date());
      const dateKey = dateObj.toISOString().split('T')[0];
      if (!groupedMessages[dateKey]) groupedMessages[dateKey] = [];
      groupedMessages[dateKey].push(message);
    });

    return groupedMessages;
  });

  groupedMessages: Signal<{ date: string; messages: SupportMessage[] }[]> = computed(() => {
    const groups = this.groupMessages();
    const sortedDates = Object.keys(groups).sort();

    return sortedDates.map(date => ({
      date,
      messages: groups[date]
    }));
  });

  basePath: Signal<string> = computed(() => {
    return `supportApp/media/${this.cc.user.uid}/chatMessages`;
  });

  cloudImagesRecord: WritableSignal<Record<number, string>> = signal({});
  localImagesRecord: WritableSignal<Record<number, string>> = signal({});
  imagesRecord: Signal<Record<number, string>> = computed(() => {
    const cloudImagesRecord = this.cloudImagesRecord();
    const localImagesRecord = this.localImagesRecord();
    return { ...cloudImagesRecord, ...localImagesRecord };
  });

  messageForm: FormGroup;
  uploadingMessages: WritableSignal<SupportMessage[]> = signal([]);
  failedMessageIds: WritableSignal<Set<string>> = signal(new Set());
  uploadingMessageIds: Signal<string[]> = computed(() => {
    const postedMessageIds = this.ss.supportMessages().map(message => message.id);
    const notSentMessageIds = this.uploadingMessages().map(message => message.id);
    return notSentMessageIds.filter(id => !postedMessageIds.includes(id));
  });

  // Unread message tracking
  unreadCount: Signal<number> = computed(() => {
    const messages = this.ss.supportMessages();
    return messages.filter(m => m.source === 'support').length;
  });

  newAttachments!: FormGroup;

  screenEnterTime: Signal<Date> = signal(new Date());
  unSubscribe = new Subject<void>();

  constructor(
    public ss: SettingsService,
    private dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private fbService: FirebaseService,
    private fb: FormBuilder,
    private dataTransform: DataTransformService,
    private fbfs: FirebaseFunctionService,
    private consolidatedApi: ConsolidatedApiService,
    private attachmentsService: AttachmentsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private utils: UtilsService,
    private modalService: ModalService,
    private router: Router,
  ) {
    this.newAttachments = this.fb.group({});
    effect(() => {
      const messages = this.ss.supportMessages();
      const uploading = this.uploadingMessages();

      if (messages.length > 0 || uploading.length > 0) {
        const attachments: Record<string, Attachment[]> = {};
        messages.forEach(message => {
          if (message.attachments) {
            attachments[message.id] = Object.values(message.attachments);
          }
        });
        this.getAttachmentSrc(attachments);

        // Scroll to the bottom whenever any message array changes
        setTimeout(() => this.scrollToBottom(), 100);
      }
    });

    effect(() => {
      const syncedIds = new Set(this.ss.supportMessages().map(m => m.id));
      const currentUploading = this.uploadingMessages();

      const stillUploading = currentUploading.filter(m => !syncedIds.has(m.id));

      if (stillUploading.length !== currentUploading.length) {
        untracked(() => {
          this.uploadingMessages.set(stillUploading);
        });
      }
    });

    this.messageForm = this.fb.group({
      id: new FormControl(getNewId()),
      text: new FormControl('', [Validators.maxLength(640), notOnlyWhitespace()]),
      attachments: this.fb.group({}),
    });
  };

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  private scrollToBottom(): void {
    try {
      const el = this.scrollContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    } catch (err) { }
  }

  ngOnInit() {
    this.analyticsInit();
    this.markChatRead();
  }

  navigateToToday() {
    this.router.navigate(['/today']);
  }

  get attachments(): FormGroup {
    return this.messageForm.get('attachments') as FormGroup;
  }

  getAttachments(attachments: Record<string, Attachment>): Attachment[] {
    return attachments ? Object.values(attachments) : [];
  }

  get attachmentValues(): Attachment[] {
    const newAttachments = Object.values(this.newAttachments.value);
    return newAttachments as Attachment[];
  }

  deleteAttachments(attachments: Attachment[]) {
    attachments?.forEach(attach => {
      this.newAttachments.removeControl(attach.id.toString());
    });
  }

  getGroupDateLabel(dateString: string): string {
    const date = new Date(dateString + 'T00:00:00');
    return getDateLabel(
      date,
      this.ss.texts(),
      (key, vars) => this.ss.t(key as string, vars),
      false,
      this.ss.appLanguageCode()
    );
  }

  openInstructions() {
    this.dialog.open(SupportInstructionsComponent, {
      width: '100%',
      maxWidth: '400px',
    });
  };

  messageFc(): FormControl {
    return this.messageForm.get('text') as FormControl;
  }

  getAttachmentSrc(attachments: Record<string, Attachment[]>) {
    const messageIds = Object.keys(attachments);
    const attachmentPaths: Record<number, string> = [];

    messageIds.forEach(messageId => {
      attachments[messageId].forEach(att => {
        if (att.fileType === 'image' && att.status === 'cloud' && !att.deletedAt && !this.cloudImagesRecord()[att.id]) {
          const path = this.ds.getAttachmentPath(`${this.basePath()}/${messageId}`, att, 'optimizedFiles');
          attachmentPaths[att.id] = path;
        }
      });
    });

    if (Object.keys(attachmentPaths).length !== 0) {
      this.fbService.getFiles(attachmentPaths)
        .then((fileRecord: Record<number, string>) => {
          this.cloudImagesRecord.set({ ...this.cloudImagesRecord(), ...fileRecord });
        })
        .catch(err => {
          log.error('ChatComponent', 'Failed to get attachment files', err);
        });
    };
  };

  clearMessageForm() {
    this.messageForm.reset();
  };

  async sendMessage() {
    if ((!this.messageFc().value?.trim()) && this.attachmentValues.length === 0) {
      return;
    }

    const messageText = this.messageFc().value?.trim();
    const messageId = getNewId();

    const newAttachments: Attachment[] = Object.values(this.newAttachments.value);
    const now = new Date();

    // 1. Prepare local attachments for immediate display
    const localAttachments: Record<string, Attachment> = {};
    if (newAttachments.length !== 0) {
      newAttachments.forEach(attach => {
        localAttachments[attach.id] = attach;
        this.updateLocalImage(attach.id, this.getFileRecord(attach));
      });
    }

    // 2. Create message for immediate display
    const message: SupportMessage = {
      id: messageId,
      text: messageText || '',
      lang: this.ss.appLanguageCode(),
      attachments: localAttachments,
      localUpdatedAt: now,
      cloudUpdatedAt: now,
      createdAt: now,
      entityType: 'chatMessages',
      entityVer: EntityVersions.chatMessages,
      sessionId: getNewId(),
      source: 'user',
      uid: this.cc.user.uid,
      senderUid: this.cc.user.uid,
      permaDeletedAt: null,
      deletedAt: null,
    };

    // 3. Clear UI Inputs immediately
    this.messageFc().setValue('');
    this.newAttachments = this.fb.group({});

    // 4. Add to "Uploading" list IMMEDIATELY
    this.uploadingMessages.update(prev => [...prev, message]);

    // 5. Upload attachments in background (if any)
    const attachmentValues: Record<string, Attachment> = {}
    if (newAttachments.length !== 0) {
      try {
        const attachments = await this.attachmentsService.updateAttachments(newAttachments, `${this.basePath()}/${messageId}`);
        const attachmentIds = Object.keys(attachments.value);

        attachmentIds.forEach(id => {
          attachmentValues[id] = attachments.get(id)?.value;
        });

        message.attachments = attachmentValues;
      } catch (error) {
        log.error('ChatComponent', 'Failed to upload attachments', error);
        this.failedMessageIds.update(ids => new Set([...ids, messageId]));
        return;
      }
    }

    // 6. Upload message to Firestore
    try {
      this.uploadingMessages.update(prev =>
        prev.map(m => m.id === messageId ? { ...m, attachments: attachmentValues } : m)
      );

      const syncRequest = this.dataTransform.prepareRawData({ ...message, attachments: attachmentValues });
      await this.fbfs.uploadData(syncRequest);
    } catch (error) {
      log.error('ChatComponent', 'Failed to send message', error);
      this.failedMessageIds.update(ids => new Set([...ids, messageId]));
    }
  }

  updateLocalImage(id: number, url: string) {
    this.localImagesRecord.update(prev => ({
      ...prev,
      [id]: url,
    }));
  }

  getFileRecord(attach: Attachment): string {
    if (attach.fileType === 'image' && attach.optimizedFile) {
      return URL.createObjectURL(new Blob([attach.optimizedFile as Uint8Array]));
    } else {
      return '';
    }
  }

  async sendLogs(logAttachment: Attachment) {
    const messageId = getNewId();
    const message: SupportMessage = {
      id: messageId,
      text: '',
      lang: this.ss.appLanguageCode(),
      attachments: { [logAttachment.id]: logAttachment },
      localUpdatedAt: new Date(),
      cloudUpdatedAt: null,
      createdAt: new Date(),
      entityType: 'chatMessages',
      entityVer: EntityVersions.chatMessages,
      sessionId: getNewId(),
      source: 'user',
      uid: this.cc.user.uid,
      senderUid: this.cc.user.uid,
      permaDeletedAt: null,
      deletedAt: null,
    };

    const localMessage = { ...message };
    this.uploadingMessages.set([...this.uploadingMessages(), localMessage]);
    const attachments = await this.attachmentsService.updateAttachments([logAttachment], `${this.basePath()}/${messageId}`);
    const attachmentIds = Object.keys(attachments.value);
    const attachmentValues: Record<string, Attachment> = {}
    attachmentIds.forEach(id => {
      attachmentValues[id] = attachments.get(id)?.value;
    });
    const syncRequest = this.dataTransform.prepareRawData({ ...message, attachments: attachmentValues });
    try {
      await this.fbfs.uploadData(syncRequest);
    } catch (error) {
      log.error('ChatComponent', 'Failed to send logs', error);
    }
  };

  viewAttachments(index: number, message: SupportMessage, attachment: Attachment) {
    if (attachment.fileType === 'txt' && attachment.originalFileName?.includes('LOG')) {
      return;
    }

    const data = new AttachmentDialogModel(
      null,
      message.id,
      index,
      Object.values(message.attachments),
      `${this.basePath()}/${message.id}`,
      true,
    )

    if (this.cc.isNarrowScreen()) {
      this.modalService.openMainContent(
        AttachmentViewComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    } else {
      this.modalService.openModalLevel1(
        AttachmentViewComponent,
        data,
        { disableClose: true, autoFocus: false }
      );
    }
  }

  /**
   * Mark chat as read by the user when entering the chat screen
   */
  private async markChatRead() {
    try {
      const uid = this.cc.user.uid;
      await this.consolidatedApi.call('markChatRead', { uid });
    } catch (error) {
      log.warn('ChatComponent', 'Failed to mark chat as read', error);
    }
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('drawerSupport', previousScreen);
        this.storageService.setCurrentScreen('drawerSupport');
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('drawerSupport', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
